import { createContext, useContext, useEffect, useState, useCallback, type ReactNode } from "react";
import { api } from "../api";
import { useAuth } from "../auth";
import { OPPORTUNITY_STATUS_LABELS, DEAL_STATUS_LABELS } from "@shared/types";

// 提案ステータス（商談・案件）を組織設定から取得して、ラベル・色・選択肢を提供する。
// 取得前や未ログイン時は shared のenumラベルにフォールバックする。
export type StatusTone = "gray" | "green" | "red" | "amber" | "blue";
export type StatusDomain = "opportunity" | "deal";
export interface StatusDef { id: string; domain: string; key: string; label: string; color: StatusTone; sort_order: number; active: number; builtin: number; }

const staticLabel = (domain: StatusDomain, key: string): string =>
  domain === "opportunity"
    ? ((OPPORTUNITY_STATUS_LABELS as Record<string, string>)[key] ?? key)
    : ((DEAL_STATUS_LABELS as Record<string, string>)[key] ?? key);

interface StatusCtxValue {
  opp: StatusDef[];
  deal: StatusDef[];
  list: (domain: StatusDomain) => StatusDef[];
  labelOf: (domain: StatusDomain, key: string) => string;
  toneOf: (domain: StatusDomain, key: string) => StatusTone;
  refresh: () => void;
}

const Ctx = createContext<StatusCtxValue>({
  opp: [], deal: [],
  list: () => [],
  labelOf: staticLabel,
  toneOf: () => "gray",
  refresh: () => { /* noop */ },
});

export function StatusProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const [opp, setOpp] = useState<StatusDef[]>([]);
  const [deal, setDeal] = useState<StatusDef[]>([]);

  const refresh = useCallback(() => {
    if (!user) return;
    api.get<{ opportunity: StatusDef[]; deal: StatusDef[] }>("/statuses")
      .then((d) => { setOpp(d.opportunity ?? []); setDeal(d.deal ?? []); })
      .catch(() => { /* フォールバックに任せる */ });
  }, [user]);
  useEffect(() => { refresh(); }, [refresh]);

  const list = useCallback((domain: StatusDomain) => (domain === "opportunity" ? opp : deal), [opp, deal]);
  const labelOf = useCallback((domain: StatusDomain, key: string) => {
    const f = (domain === "opportunity" ? opp : deal).find((s) => s.key === key);
    return f?.label ?? staticLabel(domain, key);
  }, [opp, deal]);
  const toneOf = useCallback((domain: StatusDomain, key: string): StatusTone => {
    const f = (domain === "opportunity" ? opp : deal).find((s) => s.key === key);
    return (f?.color as StatusTone) ?? "gray";
  }, [opp, deal]);

  return <Ctx.Provider value={{ opp, deal, list, labelOf, toneOf, refresh }}>{children}</Ctx.Provider>;
}

export const useStatuses = () => useContext(Ctx);
